import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dividiTesto, oggiARoma, righeScritte, type ProgettoDivisore } from '@masamune/shared';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useAuthStore } from '../../stores/auth.store';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  assignableUsersQuery, clientsLiteQuery,
  RECURRENCES, RECURRENCE_LABEL, fromDateTimeInput, fullName,
  type TaskRecurrenceKey,
} from '../../features/tasks/tasksApi';
import { useTaskActions } from '../../features/tasks/useTaskActions';
import { leggibile } from '../mobile/MobileKit';
import { AnteprimaTask, corpoDi, nomeBreve, righeAnteprima, type Scelte } from './AnteprimaTask';

interface Prefill {
  title?: string;
  description?: string;
  priority?: string;
  /** Più righe incollate nell'aggiunta veloce: il testo da dividere in task. */
  testo?: string;
  /** "Assegnato a" già scelto altrove ('' = nessuno; se manca, chi crea). */
  assigneeId?: string;
  /** "Scadenza" già scelta altrove, 'YYYY-MM-DD'. */
  dueDate?: string;
}

/** Il server crea al massimo 50 task alla volta (createTasksBulkInput). */
const MAX_TASK = 50;

// I progetti servono al divisore: "Villa Fago — Digital Marketing 2026" porta al cliente GVF.
// Chi non vede i progetti li ha vuoti, e il divisore fa a meno di loro.
const progettiLiteQuery = {
  queryKey: ['tasks', 'projects-lite'] as const,
  queryFn: async (): Promise<ProgettoDivisore[]> => {
    const r = await api.get<{ items?: Array<{ id: string; name: string; client?: { id: string } | null }> }>('/projects?pageSize=200');
    return (r.items ?? []).map((p) => ({ id: p.id, name: p.name, clientId: p.client?.id ?? null }));
  },
  staleTime: 5 * 60_000,
  retry: false,
};

/**
 * Nuovo task: solo l'essenziale. Titolo, assegnato a (default: chi crea),
 * scadenza, promemoria e ricorrenza facoltativi, priorità Normale/Alta,
 * descrizione facoltativa, cliente in fondo. Il progetto non si sceglie qui:
 * arriva già impostato quando si crea dalla kanban di progetto.
 * Salva con Ctrl/Cmd+Invio.
 *
 * Più cose insieme (7/10/2026): il titolo va a capo, e da due righe in su
 * ogni riga è un task. Sotto il titolo si vede cosa diventa ciascuna (cliente,
 * persona, scadenza, urgenza letti dal divisore, packages/shared/tasks), si
 * corregge, e un tasto solo "Crea N task" li crea tutti (POST /tasks/bulk).
 * Con una riga sola la finestra, la chiamata e il toast sono quelli di prima.
 */
export function NewTaskModal({ onClose, projectId, prefill }: { onClose: () => void; projectId?: string; prefill?: Prefill }) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const [form, setForm] = useState({
    title: prefill?.testo ?? prefill?.title ?? '',
    description: prefill?.description ?? '',
    assigneeId: prefill?.assigneeId !== undefined ? prefill.assigneeId : me?.id ?? '',
    dueDate: prefill?.dueDate ?? '',
    remindAt: '',
    recurrence: 'NONE' as TaskRecurrenceKey,
    high: prefill?.priority === 'ALTA',
    clientId: '',
  });

  const { data: clients } = useQuery(clientsLiteQuery);
  const { data: users } = useQuery(assignableUsersQuery);
  const isMobile = useIsMobile();
  const [altri, setAltri] = useState(false);
  const mostraAltri = !isMobile || altri;

  // Una riga = un task, come sempre. Da due righe scritte in su: l'anteprima.
  const multi = righeScritte(form.title) >= 2;
  const { data: progetti } = useQuery({ ...progettiLiteQuery, enabled: multi });
  const [scelte, setScelte] = useState<Record<string, Scelte>>({});
  const [errore, setErrore] = useState<string | null>(null);
  const oggi = useMemo(() => oggiARoma(), []);
  const contesto = useMemo(
    () => ({ oggi, io: me?.id ?? '', persone: users ?? [], clienti: clients?.items ?? [], progetti: progetti ?? [] }),
    [oggi, me?.id, users, clients, progetti],
  );
  const divisi = useMemo(() => (multi ? dividiTesto(form.title, contesto) : []), [multi, form.title, contesto]);
  const righe = useMemo(
    () => righeAnteprima(divisi, scelte, { clientId: form.clientId || null, assigneeId: form.assigneeId || null, dueDate: form.dueDate || null }),
    [divisi, scelte, form.clientId, form.assigneeId, form.dueDate],
  );
  const opz = useMemo(() => ({
    oggi,
    meId: me?.id ?? '',
    clienti: (clients?.items ?? []).map((c) => {
      const nome = leggibile(c.shortName || c.legalName);
      return { id: c.id, nome, breve: nomeBreve(nome) };
    }),
    persone: (users ?? []).map((u) => ({ id: u.id, nome: `${fullName(u)}${u.id === me?.id ? ' (io)' : ''}`, breve: `${u.firstName.charAt(0)}. ${u.lastName}` })),
  }), [oggi, me?.id, clients, users]);

  const cambia = (chiave: string | null, s: Scelte) => {
    setErrore(null);
    setScelte((prima) => {
      if (chiave) return { ...prima, [chiave]: { ...prima[chiave], ...s } };
      // "Spunta tutte" / "Togli tutte le spunte": su ogni riga che si può creare.
      const tutte = { ...prima };
      for (const r of righe) if (!r.problema) tutte[r.chiave] = { ...tutte[r.chiave], ...s };
      return tutte;
    });
  };

  // Una riga sola: il titolo così com'è, anche se c'è un a capo vuoto o un puntino da solo.
  const titoloSolo = form.title.replace(/\s*\n\s*/g, ' ').trim();
  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>('/tasks', {
      title: titoloSolo,
      description: form.description.trim() || null,
      projectId: projectId ?? null,
      clientId: form.clientId || null,
      status: 'TODO',
      priority: form.high ? 'ALTA' : 'MEDIA',
      assigneeId: form.assigneeId || null,
      dueDate: form.dueDate || null,
      remindAt: fromDateTimeInput(form.remindAt),
      recurrence: form.recurrence,
      isPrivate: false,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast.ok('Task creato', titoloSolo);
      onClose();
    },
    onError: (err) => toast.alert('Task non creato', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const { createMany } = useTaskActions();
  const spuntate = righe.filter((r) => r.spuntata);
  const n = spuntate.length;
  const daSistemare = spuntate.find((r) => r.problema);
  // Quello che il server rifiuterebbe si dice qui, prima: il lotto si crea tutto o niente.
  const limite = !multi ? null
    : n > MAX_TASK ? `Al massimo ${MAX_TASK} task alla volta: togline ${n - MAX_TASK}`
    : daSistemare ? `Una riga va sistemata prima di creare: "${daSistemare.d.testo.slice(0, 60)}"`
    : null;
  const creaTutti = () => {
    setErrore(null);
    createMany.mutate(spuntate.map((r) => corpoDi(r, projectId ?? null)), {
      onSuccess: () => onClose(),
      // La finestra resta aperta con il testo e le spunte; il messaggio si legge qui, sopra il tasto.
      onError: (err) => {
        const msg = err instanceof ApiError ? err.message : 'Non riesco a raggiungere il server: riprova';
        setErrore(msg === 'Validation failed' ? 'Alcune righe non vanno bene: controlla quelle segnate' : msg);
      },
    });
  };

  const canSubmit = multi
    ? n > 0 && !limite && !createMany.isPending
    : !!titoloSolo && !create.isPending;
  const invia = () => {
    if (!canSubmit) return;
    if (multi) creaTutti();
    else create.mutate();
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); invia(); }
  };
  const etichettaTasto = !multi ? 'Crea task'
    : createMany.isPending ? `Creo ${n} task…`
    : n === 0 ? 'Nessun task spuntato'
    : `Crea ${n} task`;

  // Il titolo cresce con il testo fino a sei righe, poi scorre. Con una riga è alto come il campo di prima.
  const titoloRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = titoloRef.current;
    if (!el) return;
    const cs = getComputedStyle(el);
    const riga = parseFloat(cs.lineHeight) || 20;
    const bordi = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    const vuoto = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const massimo = riga * 6 + vuoto + bordi;
    el.style.height = 'auto';
    const voluta = el.scrollHeight + bordi;
    el.style.height = `${Math.min(voluta, massimo)}px`;
    el.style.overflowY = voluta > massimo ? 'auto' : 'hidden';
  }, [form.title, isMobile]);

  const campiPredefiniti = (
    <>
      <Field label="Assegnato a" span={6}>
        <select className="inp" value={form.assigneeId} onChange={(e) => setForm({ ...form, assigneeId: e.target.value })}>
          <option value="">Nessuno</option>
          {(users ?? []).map((u) => <option key={u.id} value={u.id}>{fullName(u)}{u.id === me?.id ? ' (io)' : ''}</option>)}
        </select>
      </Field>
      <Field label="Scadenza" span={6}>
        <input className="inp mono" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
      </Field>
    </>
  );
  const campoCliente = (hint?: string) => (
    <Field label="Cliente" span={12} hint={hint}>
      <select className="inp" value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })}>
        <option value="">Nessuno</option>
        {(clients?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.shortName}</option>)}
      </select>
    </Field>
  );

  return (
    <Modal
      title="Nuovo task"
      kicker="Task"
      onClose={onClose}
      size="md"
      // Con l'anteprima la finestra si allarga (nuovo-task.css): le quattro etichette di una riga
      // stanno su una riga sola. Non size="lg": .modal-lg Tailwind la scarta (vedi lavori.css).
      className={multi ? 'modal--anteprima' : undefined}
      footer={
        <>
          {multi && (errore || limite) && (
            <div className="nt-avviso" role="alert">
              <Icon name="alert" size={14} />
              <span>{errore ?? limite}</span>
            </div>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={invia} disabled={!canSubmit}>
            {etichettaTasto} {canSubmit && <Icon name="arrowRight" size={12} />}
          </button>
        </>
      }
    >
      <div className="form-grid" onKeyDown={onKeyDown}>
        <Field
          label="Titolo"
          required
          span={12}
          hint={!multi ? undefined
            : isMobile ? 'Ogni riga è un task: qui sotto cosa diventa. Si corregge toccando la riga.'
            : 'Ogni riga è un task: qui sotto cosa diventa. Si corregge con un clic sulle etichette.'}
        >
          <textarea
            ref={titoloRef}
            className="inp nt-titolo"
            rows={1}
            autoFocus
            placeholder="Cosa va fatto, una cosa per riga"
            aria-label="Titolo"
            value={form.title}
            onChange={(e) => { setErrore(null); setForm({ ...form, title: e.target.value }); }}
          />
        </Field>
        {multi ? (
          <>
            <div style={{ gridColumn: 'span 12' }}>
              <AnteprimaTask righe={righe} opz={opz} mobile={isMobile} onCambia={cambia} />
            </div>
            <div className="nt-predefiniti" style={{ gridColumn: 'span 12' }}>
              Per le righe che non dicono chi lo fa, la scadenza o il cliente valgono questi:
            </div>
            {campiPredefiniti}
            {campoCliente()}
          </>
        ) : (
          <>
            {campiPredefiniti}
            {/* Sul telefono un task si crea al volo: titolo, chi lo fa e scadenza;
                il resto dietro "Altri dettagli". Sul computer mostraAltri è sempre
                vero e la finestra è quella di prima, campo per campo. */}
            {!mostraAltri && (
              <div style={{ gridColumn: 'span 12' }}>
                <button type="button" className="nt-altri" onClick={() => setAltri(true)}>
                  <span>
                    <strong>Altri dettagli</strong>
                    <em>promemoria, ricorrenza, priorità, note, cliente</em>
                  </span>
                  <Icon name="chevronD" size={16} />
                </button>
              </div>
            )}
            {mostraAltri && (<>
            <Field label="Promemoria" span={6} hint="Facoltativo, con l'ora">
              <input className="inp mono" type="datetime-local" value={form.remindAt} onChange={(e) => setForm({ ...form, remindAt: e.target.value })} />
            </Field>
            <Field label="Ricorrenza" span={6} hint="Facoltativa: al Completa si crea il prossimo">
              <select className="inp" value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value as TaskRecurrenceKey })}>
                {RECURRENCES.map((r) => <option key={r} value={r}>{RECURRENCE_LABEL[r]}</option>)}
              </select>
            </Field>
            <Field label="Priorità" span={12}>
              <div className="seg-control">
                <button type="button" aria-pressed={!form.high} onClick={() => setForm({ ...form, high: false })}>Normale</button>
                <button type="button" aria-pressed={form.high} onClick={() => setForm({ ...form, high: true })}>Alta</button>
              </div>
            </Field>
            <Field label="Descrizione" span={12}>
              <textarea className="inp" rows={3} placeholder="Facoltativa: dettagli utili a chi lo farà" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            {campoCliente('Facoltativo')}
            </>)}
          </>
        )}
      </div>
    </Modal>
  );
}
